import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Users, Calendar, Shield, ArrowRight, Clock, MapPin, Building2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default gradient background (no hardcoded images)
const DEFAULT_GRADIENT = "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)";

// Helper to get image URL
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API}${path}`;
};

// Timezone-safe date formatting
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  // Parse as local date to avoid timezone shift
  const [year, month, day] = dateStr.split('T')[0].split('-');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { 
    weekday: "long", 
    month: "long", 
    day: "numeric", 
    year: "numeric" 
  });
};

// Format time to 12-hour format
const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const JoinTeamPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, login, logout } = useAuth();
  
  const [status, setStatus] = useState("loading"); // loading, valid, accepting, success, error, need_auth
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [authEmail, setAuthEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  // Check for return from magic link auth
  useEffect(() => {
    const joinRedirect = localStorage.getItem("join_redirect");
    if (isAuthenticated && joinRedirect && joinRedirect.includes(token)) {
      localStorage.removeItem("join_redirect");
      // Auto-accept after returning from auth
      acceptInvitation();
    }
  }, [isAuthenticated, token]);

  // Show current auth status if user is already logged in
  const showAuthMismatch = isAuthenticated && inviteData?.invite?.email && 
    user?.email?.toLowerCase() !== inviteData.invite.email.toLowerCase();

  const validateToken = async () => {
    try {
      setStatus("loading");
      const res = await fetch(`${API}/join/team/${token}`);
      
      if (!res.ok) {
        let errorMsg = "Invalid invitation";
        try {
          const data = await res.json();
          errorMsg = data.detail || errorMsg;
        } catch (e) {
          errorMsg = `Server error (${res.status})`;
        }
        throw new Error(errorMsg);
      }
      
      const data = await res.json();
      setInviteData(data);
      // Pre-fill email if invite has one
      if (data.invite?.email) {
        setAuthEmail(data.invite.email);
      }
      // Also try invited_email
      if (data.invited_email) {
        setAuthEmail(data.invited_email);
      }
      setStatus("valid");
    } catch (err) {
      setStatus("error");
      // Show more helpful error message
      if (err.message === "Failed to fetch") {
        setError("Unable to connect to server. Please check your internet connection and try again.");
      } else {
        setError(err.message);
      }
    }
  };

  const sendMagicLink = async () => {
    if (!authEmail) {
      toast.error("Please enter your email");
      return;
    }
    
    try {
      setSendingMagicLink(true);
      const res = await fetch(`${API}/auth/send-magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: authEmail,
          redirect_url: `/join/team/${token}`
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to send magic link");
      }
      
      setMagicLinkSent(true);
      toast.success("Magic link sent! Check your email.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingMagicLink(false);
    }
  };

  const acceptInvitation = async () => {
    if (!isAuthenticated) {
      // Show auth flow instead of redirecting to login
      setStatus("need_auth");
      return;
    }

    try {
      setStatus("accepting");
      const sessionToken = localStorage.getItem("session_token");
      
      const res = await fetch(`${API}/join/team/${token}/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`
        }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to accept invitation");
      }
      
      const data = await res.json();
      
      // Store team membership in localStorage for route access
      try {
        const teamEvents = JSON.parse(localStorage.getItem('team_events') || '[]');
        if (!teamEvents.includes(data.event_id)) {
          teamEvents.push(data.event_id);
          localStorage.setItem('team_events', JSON.stringify(teamEvents));
        }
      } catch (e) {
        console.error('Failed to store team event:', e);
      }
      
      setStatus("success");
      toast.success("You've joined the team!");
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate(data.redirect_url || `/host/v2/${inviteData.event.event_id}`);
      }, 2000);
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        
        {/* Loading */}
        {status === "loading" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-gray-900 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Verifying invitation...</h1>
          </Card>
        )}

        {/* Valid Invitation */}
        {status === "valid" && inviteData && (
          <Card className="overflow-hidden">
            {/* Event Cover - Use event cover or gradient (no hardcoded images) */}
            <div 
              className="relative aspect-square w-full max-h-48 overflow-hidden"
              style={inviteData.event?.cover_image 
                ? { backgroundImage: `url(${getImageUrl(inviteData.event.cover_image)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                : { background: DEFAULT_GRADIENT }
              }
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              
              {/* Logo overlay */}
              {inviteData.event?.logo_image && (
                <div className="absolute bottom-3 left-3">
                  <div className="w-12 h-12 rounded-lg bg-white p-1 shadow-lg">
                    <img 
                      src={getImageUrl(inviteData.event.logo_image)} 
                      alt="" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <CardContent className="p-6 space-y-5">
              {/* Invitation Header */}
              <div className="text-center space-y-2">
                <div className="w-12 h-12 mx-auto bg-purple-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">
                  You're Invited to Join the Team!
                </h1>
                <p className="text-gray-600 text-sm">
                  <span className="font-medium">{inviteData.host_name}</span> has invited you to help manage
                </p>
              </div>

              {/* Event Info */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h2 className="font-semibold text-lg text-gray-900">{inviteData.event?.name}</h2>
                
                {/* Date & Time */}
                {inviteData.event?.date && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {formatDate(inviteData.event.date)}
                      {inviteData.event.start_time && (
                        <span className="ml-1">
                          • {formatTime(inviteData.event.start_time)}
                          {inviteData.event.timezone_abbr && ` ${inviteData.event.timezone_abbr}`}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                
                {/* Location */}
                {(inviteData.event?.venue_name || inviteData.event?.city) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {inviteData.event.venue_name}
                      {inviteData.event.venue_name && inviteData.event.city && ", "}
                      {inviteData.event.city}
                    </span>
                  </div>
                )}
                
                {/* Hosts - show all hosts including placeholders */}
                {(() => {
                  const hosts = inviteData.all_hosts || [];
                  // Fallback to org_name if no all_hosts
                  if (hosts.length === 0 && inviteData.org_name) {
                    hosts.push({ name: inviteData.org_name, type: "main" });
                  }
                  if (hosts.length > 0) {
                    const hostNames = hosts.map(h => h.name).join(" & ");
                    return (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Building2 className="w-4 h-4 flex-shrink-0" />
                        <span>Hosted by {hostNames}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Role Info */}
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900 capitalize">
                    {inviteData.role === "host" ? "Co-Host" : "Team Member"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {inviteData.role === "host" 
                      ? "Full access to event dashboard and settings" 
                      : "Access to attendee management and check-in"}
                  </p>
                </div>
              </div>

              {/* Invited As */}
              {inviteData.invited_name && (
                <div className="text-center text-sm text-gray-500">
                  Invited as: <span className="font-medium">{inviteData.invited_name}</span>
                  {inviteData.invited_email && !inviteData.invited_email.includes("placeholder") && (
                    <span> ({inviteData.invited_email})</span>
                  )}
                </div>
              )}

              {/* CTA */}
              <Button 
                onClick={acceptInvitation}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3"
                data-testid="accept-team-invite-btn"
              >
                {isAuthenticated ? (
                  <>
                    Accept & Join Team
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Sign In to Accept
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              {/* Show current auth status */}
              {isAuthenticated && user?.email && (
                <div className="text-center text-xs text-gray-500">
                  Signed in as <span className="font-medium">{user.email}</span>
                  <Button
                    onClick={() => {
                      logout();
                      setStatus("need_auth");
                      if (inviteData?.invite?.email) {
                        setAuthEmail(inviteData.invite.email);
                      }
                    }}
                    variant="link"
                    size="sm"
                    className="text-gray-400 text-xs px-1 h-auto"
                  >
                    (sign out)
                  </Button>
                </div>
              )}

              {isAuthenticated && user?.email && inviteData.invited_email && 
               !inviteData.invited_email.includes("placeholder") &&
               user.email.toLowerCase() !== inviteData.invited_email.toLowerCase() && (
                <div className="text-center space-y-2">
                  <p className="text-xs text-amber-600">
                    Note: The invitation was sent to <span className="font-medium">{inviteData.invited_email}</span>.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Accepting */}
        {status === "accepting" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-gray-900 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Joining team...</h1>
          </Card>
        )}

        {/* Success */}
        {status === "success" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Welcome to the team!</h1>
            <p className="text-gray-600 mt-2">Redirecting to the dashboard...</p>
          </Card>
        )}

        {/* Need Authentication */}
        {status === "need_auth" && inviteData && (
          <Card className="overflow-hidden">
            <CardContent className="p-6 space-y-5">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">
                  Verify Your Email
                </h1>
                <p className="text-gray-600 text-sm">
                  Enter your email to receive a magic link and join the team for <span className="font-medium">{inviteData.event?.name}</span>
                </p>
              </div>

              {!magicLinkSent ? (
                <div className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    className="w-full"
                    data-testid="team-auth-email-input"
                  />
                  <Button 
                    onClick={sendMagicLink}
                    disabled={sendingMagicLink || !authEmail}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                    data-testid="send-team-magic-link-btn"
                  >
                    {sendingMagicLink ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Send Magic Link
                  </Button>
                </div>
              ) : (
                <div className="text-center space-y-3">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-green-800 font-medium">Magic link sent!</p>
                    <p className="text-green-600 text-sm">Check your email at <span className="font-medium">{authEmail}</span></p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Click the link in your email, then return to this page to complete joining the team.
                  </p>
                  <Button 
                    onClick={() => setMagicLinkSent(false)}
                    variant="ghost"
                    size="sm"
                    className="text-gray-500"
                  >
                    Use a different email
                  </Button>
                </div>
              )}

              <Button 
                onClick={() => setStatus("valid")}
                variant="ghost"
                className="w-full text-gray-500"
              >
                ← Back to invitation
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {status === "error" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Invitation Error</h1>
            <p className="text-gray-600 mt-2">{error || "This invitation link is invalid or has expired."}</p>
            <Button 
              onClick={() => navigate("/")}
              variant="outline"
              className="mt-4"
            >
              Go Home
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default JoinTeamPage;
